import { useState } from "react"
import { usePage } from "@inertiajs/react"
import { ArrowLeft, Upload, FileText, X, CheckCircle } from "lucide-react"
import { CompanyLayout } from "~/components/company/CompanyLayout"
import { Button } from "~/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card"
import { Input } from "~/components/ui/input"
import { Label } from "~/components/ui/label"
import { Textarea } from "~/components/ui/textarea"
import { jobsData } from "~/components/company/job_listings/mock-data"

export default function Apply() {
  const { id } = usePage<{ id: string }>().props
  const jobDetail = jobsData[id]
  const job = jobDetail
    ? { title: jobDetail.title, location: jobDetail.location, type: jobDetail.type }
    : null

  const [resumeFile, setResumeFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const isValidFileType = (file: File) => {
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ]
    return validTypes.includes(file.type)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file && isValidFileType(file)) setResumeFile(file)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && isValidFileType(file)) setResumeFile(file)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitted(true)
  }

  if (!job) {
    return (
      <CompanyLayout>
        <div className="flex flex-1 flex-col items-center justify-center py-32">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Job not found</h1>
          <a href="/careers">
            <Button variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Careers
            </Button>
          </a>
        </div>
      </CompanyLayout>
    )
  }

  if (isSubmitted) {
    return (
      <CompanyLayout>
        <div className="flex flex-1 items-center justify-center py-32">
          <div className="text-center space-y-6 px-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Application Submitted!</h1>
            <p className="text-gray-600 max-w-md">
              Thank you for applying to the {job.title} position. We'll review your application and get back to you soon.
            </p>
            <a href="/careers">
              <Button className="bg-primary hover:bg-primary/90 text-white">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Careers
              </Button>
            </a>
          </div>
        </div>
      </CompanyLayout>
    )
  }

  return (
    <CompanyLayout>
      <div className="container px-8 md:px-12 lg:px-16 py-12">
        <a
          href="/careers"
          className="inline-flex items-center text-gray-600 hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Careers
        </a>

        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">Apply for {job.title}</CardTitle>
              <CardDescription>
                {job.location} • {job.type}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Personal Information</h3>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input id="firstName" name="firstName" required placeholder="John" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input id="lastName" name="lastName" required placeholder="Doe" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" name="email" type="email" required placeholder="john@example.com" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input id="phone" name="phone" type="tel" placeholder="+1 (555) 123-4567" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="linkedin">LinkedIn Profile</Label>
                    <Input id="linkedin" name="linkedin" type="url" placeholder="https://linkedin.com/in/..." />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="portfolio">Portfolio / GitHub</Label>
                    <Input id="portfolio" name="portfolio" type="url" placeholder="https://github.com/..." />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Resume *</h3>

                  {!resumeFile ? (
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                        isDragging ? "border-primary bg-primary/5" : "border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      <input
                        type="file"
                        id="resume"
                        name="resume"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <label htmlFor="resume" className="cursor-pointer">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                            <Upload className="h-6 w-6 text-gray-500" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              Drag and drop your resume here, or <span className="text-primary">browse</span>
                            </p>
                            <p className="text-sm text-gray-500 mt-1">PDF, DOC, DOCX (Max 5MB)</p>
                          </div>
                          <Button type="button" variant="outline" className="mt-2 bg-transparent">
                            <Upload className="mr-2 h-4 w-4" />
                            Upload Resume
                          </Button>
                        </div>
                      </label>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{resumeFile.name}</p>
                          <p className="text-sm text-gray-500">{(resumeFile.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setResumeFile(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Cover Letter</h3>
                  <div className="space-y-2">
                    <Label htmlFor="coverLetter">Tell us why you're interested in this role</Label>
                    <Textarea
                      id="coverLetter"
                      name="coverLetter"
                      rows={5}
                      placeholder="Share your experience, what excites you about this role, and why you'd be a great fit..."
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 text-white py-6"
                  disabled={!resumeFile}
                >
                  Submit Application
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </CompanyLayout>
  )
}
